<template>
  <div class="resume-templates-section">
    <!-- Upload Section -->
    <b-row class="mb-3">
      <b-col cols="12">
        <div class="d-flex align-items-center justify-content-between">
          <h5 class="font-weight-bolder">
            Resume Templates
          </h5>
          <div class="d-flex align-items-center">
            <b-button
              variant="warning"
              class="mr-2"
              @click="$refs.docxFileInput.click()"
            >
              <feather-icon
                icon="UploadIcon"
                size="16"
                class="mr-50"
              />
              Upload Template
            </b-button>
            <input
              ref="docxFileInput"
              type="file"
              class="d-none"
              accept=".doc,.docx"
              @change="handleFileUpload"
            >
          </div>
        </div>
        <p class="text-muted mt-1">
          Upload resume templates with custom headers and footers. Only .doc and .docx files are accepted.
        </p>
      </b-col>
    </b-row>

    <!-- Loading State -->
    <b-row
      v-if="isLoading"
      class="my-3"
    >
      <b-col
        cols="12"
        class="text-center"
      >
        <b-spinner
          variant="primary"
          label="Loading..."
        />
        <p class="mt-2 text-muted">
          Loading templates...
        </p>
      </b-col>
    </b-row>

    <!-- Templates Grid -->
    <b-row v-else-if="templates.length > 0">
      <b-col
        v-for="(template, index) in templates"
        :key="template.id"
        cols="12"
        md="4"
        class="mb-3"
      >
        <b-card
          no-body
          class="template-card h-100 shadow-sm"
          :class="{ 'border-primary': template.is_default }"
        >
          <b-card-body class="d-flex flex-column">
            <!-- Template Preview/Icon -->
            <div class="template-preview mb-2 text-center">
              <feather-icon
                icon="FileTextIcon"
                size="48"
                class="text-primary"
              />
            </div>

            <!-- Template Info -->
            <div class="template-info flex-grow-1">
              <h6 class="font-weight-bold text-truncate">
                {{ template.is_default ? 'Default Resume Template' : `Template ${index + 1}` }}
              </h6>
              <p class="text-muted small mb-1">
                <feather-icon
                  icon="UserIcon"
                  size="14"
                />
                {{ template.uploaded_by_name || 'Unknown' }}
              </p>
              <p class="text-muted small mb-1">
                <feather-icon
                  icon="CalendarIcon"
                  size="14"
                />
                {{ formatDate(template.created_at) }}
              </p>
              <b-badge
                v-if="template.is_default"
                variant="primary"
                class="mt-1"
              >
                Default Template
              </b-badge>
            </div>

            <!-- Template Actions -->
            <div class="template-actions mt-3">
              <b-button
                variant="primary"
                size="sm"
                block
                class="mb-1"
                @click="viewTemplate(template)"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="14"
                  class="mr-50"
                />
                View
              </b-button>
              <b-button
                variant="info"
                size="sm"
                block
                class="mb-1"
                @click="downloadTemplate(template)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  size="14"
                  class="mr-50"
                />
                Download
              </b-button>
              <b-button
                v-if="!template.is_default"
                variant="danger"
                size="sm"
                block
                @click="deleteTemplate(template)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="14"
                  class="mr-50"
                />
                Delete
              </b-button>
              <b-button
                v-else
                v-b-tooltip.hover
                variant="secondary"
                size="sm"
                block
                disabled
                title="Cannot delete default template"
              >
                <feather-icon
                  icon="LockIcon"
                  size="14"
                  class="mr-50"
                />
                Protected
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Empty State -->
    <b-row v-else>
      <b-col
        cols="12"
        class="text-center py-5"
      >
        <feather-icon
          icon="FileTextIcon"
          size="64"
          class="text-muted mb-3"
        />
        <h5 class="text-muted">
          No Resume Templates
        </h5>
        <p class="text-muted">
          Upload your first resume template to get started.
        </p>
      </b-col>
    </b-row>

    <!-- View Template Modal -->
    <b-modal
      v-model="viewTemplateModal"
      title="Template Preview"
      size="xl"
      hide-footer
      centered
      @close="closeTemplateViewer"
    >
      <div
        v-if="selectedTemplate"
        class="mx-2 w-full"
        style="height: 80vh !important"
      >
        <iframe
          v-if="documentUrl"
          :src="documentUrl"
          width="100%"
          style="height: 80vh !important"
          frameborder="0"
        />
        <div
          v-else
          class="d-flex justify-content-center align-items-center"
          style="height: 80vh"
        >
          <b-spinner
            variant="primary"
            label="Loading document..."
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
    name: "ResumeTemplatesSection",
    props: {
        companyUen: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            templates: [],
            viewTemplateModal: false,
            selectedTemplate: null,
            documentUrl: null,
            uploadingFile: null,
        }
    },
    mounted() {
        this.loadTemplates()
    },
    methods: {
        async loadTemplates() {
            this.isLoading = true
            try {
                const response = await this.$http.get("/api/corporate-resume-templates", {
                    params: {
                        company_uen: this.companyUen,
                    },
                })

                if (response.data.success) {
                    this.templates = response.data.output || []
                } else {
                    this.$toastDanger("Error", response.data.message || "Failed to load templates")
                }
            } catch (error) {
                console.error("Error loading templates:", error)
                this.$handleErrorResponse(error, "Error Loading Templates", null, { showToast: true })
            } finally {
                this.isLoading = false
            }
        },

        async handleFileUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            // Validate file type
            const validTypes = [
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ]
            if (!validTypes.includes(file.type)) {
                this.$toastDanger("Invalid File Type", "Please upload a .doc or .docx file")
                event.target.value = ""
                return
            }

            // Validate file size (max 10MB)
            const maxSize = 10 * 1024 * 1024
            if (file.size > maxSize) {
                this.$toastDanger("File Too Large", "Please upload a file smaller than 10MB")
                event.target.value = ""
                return
            }

            await this.uploadTemplate(file)
            event.target.value = ""
        },

        async uploadTemplate(file) {
            this.isLoading = true
            const formData = new FormData()
            formData.append("resume_template", file)
            formData.append("company_uen", this.companyUen)
            formData.append("user_id", this.$store.state.auth.ActiveUser.id)

            try {
                const response = await this.$http.post(
                    "/api/corporate-resume-templates",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                        timeout: this.$fileUploadTimeout,
                    },
                )

                if (response.data.success) {
                    this.$toastSuccess("Success", "Template uploaded successfully")
                    await this.loadTemplates()
                } else {
                    this.$toastDanger("Error", response.data.message || "Failed to upload template")
                }
            } catch (error) {
                console.error("Error uploading template:", error)
                this.$handleErrorResponse(error, "Upload Error", null, { showToast: true })
            } finally {
                this.isLoading = false
            }
        },

        async setAsDefault(template) {
            try {
                const confirmed = await this.$simpleConfirmModal(
                    this,
                    "Are you sure you want to set this as the default template?",
                    {
                        title: "Set Default Template",
                        okTitle: "Yes, Set as Default",
                    },
                )

                this.isLoading = true
                const response = await this.$http.put(
                    `/api/corporate-resume-templates/${template.id}/set-default`,
                    {
                        company_uen: this.companyUen,
                    },
                )

                if (response.data.success) {
                    this.$toastSuccess("Success", "Default template updated")
                    await this.loadTemplates()
                } else {
                    this.$toastDanger("Error", response.data.message || "Failed to update default template")
                }
            } catch (error) {
                if (error !== undefined) {
                    console.error("Error setting default template:", error)
                    this.$handleErrorResponse(error, "Error", null, { showToast: true })
                }
            } finally {
                this.isLoading = false
            }
        },

        async deleteTemplate(template) {
            try {
                await this.$simpleConfirmModal(
                    this,
                    "Are you sure you want to delete this template? This action cannot be undone.",
                    {
                        title: "Delete Template",
                        okTitle: "Yes, Delete",
                        okVariant: "danger",
                    },
                )

                this.isLoading = true
                const response = await this.$http.delete(
                    `/api/corporate-resume-templates/${template.id}`,
                    {
                        params: {
                            company_uen: this.companyUen,
                        },
                    },
                )

                if (response.data.success) {
                    this.$toastSuccess("Success", "Template deleted successfully")
                    await this.loadTemplates()
                } else {
                    this.$toastDanger("Error", response.data.message || "Failed to delete template")
                }
            } catch (error) {
                if (error !== undefined) {
                    console.error("Error deleting template:", error)
                    this.$handleErrorResponse(error, "Delete Error", null, { showToast: true })
                }
            } finally {
                this.isLoading = false
            }
        },

        viewTemplate(template) {
            console.log("Viewing template:", template)
            this.selectedTemplate = template

            // Use PDF if available, otherwise use Google Docs Viewer for DOCX
            if (template.pdf_url) {
                this.documentUrl = `${template.pdf_url}&embedded=true&random=${new Date().getTime()}`
            } else if (template.docx_url) {
                // Use Google Docs Viewer to display DOCX files
                this.documentUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
                    template.docx_url.upload_url,
                )}&embedded=true&random=${new Date().getTime()}`
            } else {
                this.documentUrl = null
            }

            this.viewTemplateModal = true
        },

        closeTemplateViewer() {
            this.documentUrl = null
            this.selectedTemplate = null
        },

        async downloadTemplate(template) {
            try {
                if (!template.docx_url) {
                    this.$toastDanger("Error", "Download URL not available")
                    return
                }

                // Create a temporary link and trigger download
                const link = document.createElement("a")
                link.href = template.docx_url
                link.download = `resume_template_${template.id}.docx`
                link.target = "_blank"
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                this.$toastSuccess("Success", "Download started")
            } catch (error) {
                console.error("Error downloading template:", error)
                this.$toastDanger("Download Error", "Failed to download template")
            }
        },

        formatDate(dateString) {
            if (!dateString || dateString === "-") {
                return "-"
            }
            return this.$formatDate(dateString)
        },
    },
}
</script>

<style lang="scss" scoped>
.resume-templates-section {
  padding: 1rem 0;
}

.template-card {
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  }

  &.border-primary {
    border-width: 2px;
  }
}

.template-preview {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.template-info {
  .small {
    font-size: 0.85rem;
    margin-bottom: 0.25rem;

    svg {
      margin-right: 0.25rem;
    }
  }
}

.template-actions {
  button {
    transition: all 0.2s ease;
  }
}

.template-preview-container {
  min-height: 600px;
}
</style>
